import { Component, For, Show, createSignal } from "solid-js";

export interface TypeSpecificFieldsProps {
  /** Work item type name (Bug, Task, Story, Epic) */
  typeName: string;
  /** Current field values - key is field name */
  values: Record<string, string>;
  /** Callback when a field value changes */
  onChange: (fieldName: string, value: string) => void;
  /** Disabled state (e.g., during form submission) */
  disabled?: boolean;
}

interface FieldConfig {
  name: string;
  label: string;
  placeholder: string;
  rows: number;
}

/**
 * Field configurations for each work item type.
 * All fields are optional textareas with guiding placeholders.
 */
const TYPE_FIELDS: Record<string, FieldConfig[]> = {
  Bug: [
    {
      name: "steps_to_reproduce",
      label: "Steps to Reproduce",
      placeholder: "1. Go to...\n2. Click on...\n3. Observe...",
      rows: 3,
    },
    {
      name: "expected_actual",
      label: "Expected vs Actual",
      placeholder: "Expected: ...\nActual: ...",
      rows: 2,
    },
  ],
  Task: [
    {
      name: "implementation_notes",
      label: "Implementation Notes",
      placeholder: "Technical approach, constraints, or dependencies...",
      rows: 2,
    },
  ],
  Story: [
    {
      name: "acceptance_criteria",
      label: "Acceptance Criteria",
      placeholder: "- Given...\n- When...\n- Then...",
      rows: 3,
    },
  ],
  Epic: [
    {
      name: "scope_notes",
      label: "Scope Notes",
      placeholder: "What's included in this epic, key deliverables...",
      rows: 2,
    },
  ],
};

/**
 * TypeSpecificFields - Renders optional type-specific input fields
 *
 * Features:
 * - Config-driven field rendering based on work item type
 * - Animated slide-down reveal
 * - Collapsible with dismiss button
 * - All fields are optional textareas
 */
export const TypeSpecificFields: Component<TypeSpecificFieldsProps> = (
  props,
) => {
  const [dismissed, setDismissed] = createSignal(false);

  const fields = () => TYPE_FIELDS[props.typeName] ?? [];
  const hasFields = () => fields().length > 0;

  // Reset dismissed state when type changes
  const currentType = () => props.typeName;
  let lastType = currentType();

  // Check if type changed and reset dismissed
  const shouldShow = () => {
    const type = currentType();
    if (type !== lastType) {
      lastType = type;
      setDismissed(false);
    }
    return hasFields() && !dismissed();
  };

  return (
    <Show when={shouldShow()}>
      <div class="animate-in fade-in-0 slide-in-from-top-2 duration-200">
        {/* Header with dismiss button */}
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs font-medium text-ink-2">
            {props.typeName} Details <span class="text-ink-3">(optional)</span>
          </span>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            class="text-ink-3 hover:text-ink transition-colors p-0.5 -mr-0.5 rounded-sm focus-ring hit-area"
            title="Dismiss"
            aria-label="Dismiss type-specific fields"
          >
            <svg
              class="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Fields */}
        <div class="space-y-2">
          <For each={fields()}>
            {(field) => (
              <div>
                <label
                  for={`field-${field.name}`}
                  class="block text-xs font-medium text-ink-2 mb-1"
                >
                  {field.label}
                </label>
                <textarea
                  id={`field-${field.name}`}
                  value={props.values[field.name] ?? ""}
                  onInput={(e) =>
                    props.onChange(field.name, e.currentTarget.value)
                  }
                  placeholder={field.placeholder}
                  rows={field.rows}
                  disabled={props.disabled}
                  class="w-full px-2 py-1.5 text-sm text-ink border border-line rounded bg-surface placeholder:text-ink-3 focus:border-ink-2 disabled:bg-sunken disabled:text-ink-3 disabled:cursor-not-allowed resize-none"
                />
              </div>
            )}
          </For>
        </div>
      </div>
    </Show>
  );
};

export default TypeSpecificFields;
